/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from 'react';
import styles from './NewUserModal.module.css';
import {
  FloComponentProps,
  FloDropdown,
  FloFormControl,
  FloFormGroup,
  FloGroupedDropdown,
  FloModal,
  FloModalBodyProps
} from 'components';
import {Formik} from 'formik';
import * as Yup from 'yup';
import Message from 'containers/common/Message';
import {Form} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {Location, LocationType, PermissionsEnum, User} from 'model';
import {
  createAccountRolesList,
  getAccountPermissions,
  getLocationPermissions,
  RoleType
} from 'model/Role';
import {ListView} from 'model/state';


const SEPARATOR = ',';
const emailRegEx = /^\S+@\S+\.\S+$/;

interface NewUserBodyProps extends FloModalBodyProps {
  createUser: (user: User) => void;
  isSubmitting: boolean;
  accountId?: string;
  userLocations: ListView<Location>;
  fetchUserLocations: (userId: string, types: LocationType[], page: number, search?: string) => void;
  userId?: string | null;
  loggedUser?: User;
}

const NewUserBody: React.FC<NewUserBodyProps> = React.memo(({
  bindSubmitForm,
  bindIsFormValid,
  createUser,
  isSubmitting,
  accountId,
  userLocations,
  fetchUserLocations,
  userId,
  loggedUser
}) => {
  const { t } = useTranslation();
  const account = accountId ? { id: accountId } : undefined;

  const [locationRolesMap, setLocationRolesMap] = useState<{[locationId: string]: PermissionsEnum[]}>({});

  // delegates all the provisioner location roles to the new user, if owner then assign write/valve* too
  const addToLocationRoleMap = (e: any) => {
    const withRoles = e?.filter((datum: any) => datum.roles !== undefined) || [];
    withRoles.forEach((datum: any) => {
      if (datum.value !== undefined) {
        const roles = (datum.roles as PermissionsEnum[]).reduce((acc: PermissionsEnum[], curr) => {
          return curr === PermissionsEnum.OWNER ?
              [...acc, PermissionsEnum.WRITE, PermissionsEnum.VALVE_CLOSE, PermissionsEnum.VALVE_OPEN] : [...acc, curr]
        }, []);

        setLocationRolesMap({
          ...locationRolesMap,
          [datum.value]: roles,
        })
      }
    });
  }

  return (
    <>
      <Message />
      <Formik
        initialValues={{
          email: '',
          role: '',
          locations: '',
          locale: '',
          emailConfirmation: '',
        }}
        onSubmit={(values, actions) => {
          const selectedLocationIds = values.locations ? values.locations.split(SEPARATOR) : undefined;
          const accountRole = {
            accountId: accountId || '',
            roles: getAccountPermissions(values.role as RoleType)
          }
          const locationRoles = selectedLocationIds?.map(locationId => ({
            locationId,
            roles: getLocationPermissions(values.role as RoleType)
          }));
          createUser({
            ...values,
            locale: values.locale === '' ? undefined : values.locale,
            accountRole,
            locationRoles,
            account
          });
          actions.setSubmitting(false);
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string().matches(emailRegEx, t('validation_email_invalid')).required(t('validation_required', { field: t('Email') })),
          locale: Yup.string().required(t('validation_required', { field: t('Locale') })),
          role: Yup.string().required(t('validation_required', { field: t('Role') })),
          emailConfirmation: Yup.string().when("email", {
            is: val => (val && val.length > 0 ? true : false),
            then: Yup.string().oneOf(
              [Yup.ref("email")], t('email_not_match'))
          }).matches(emailRegEx, t('validation_email_invalid')).required(t('validation_required', { field: t('Confirm Email') })),
        })}
      >
        {({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit, submitForm, setFieldValue, setFieldTouched }) => {
          bindSubmitForm(submitForm);
          bindIsFormValid(isValid);

          const setField = (name: string, value: any) => {
            setFieldValue(name, value, true);
            setFieldTouched(name, true, false);
          };
          return (
            <Form className={styles.form} onSubmit={handleSubmit}>

              <FloFormGroup
                controlId   = "email"
                label       = {t('Email')}
                touched     = {touched}
                errors      = {errors}
              >
                <FloFormControl
                  type="email"
                  name="email"
                  placeholder={t('Email')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  disabled={isSubmitting}
                  isInvalid={!!(touched.email && errors.email)}
                />
              </FloFormGroup>

              <FloFormGroup
                controlId   = "emailConfirmation"
                label       = {t('Confirm Email')}
                touched     = {touched}
                errors      = {errors}
              >
                <FloFormControl
                  type="email"
                  name="emailConfirmation"
                  placeholder={t('Confirm Email')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.emailConfirmation}
                  disabled={isSubmitting}
                  isInvalid={!!(touched.emailConfirmation && errors.emailConfirmation)}
                />
              </FloFormGroup>

              <FloFormGroup
                controlId   = "locale"
                label       = {t('Locale')}
                touched     = {touched}
                errors      = {errors}
              >
                <FloDropdown
                  id      = "locale"
                  title   = {t('Locale')}
                  values  = {values.locale}
                  full
                  disabled = {isSubmitting}
                  options = {[
                    { value: 'en-us', label: 'English' },
                    { value: 'fr-ca', label: 'French' },
                  ]}
                  onChange = {e => setField('locale', e.value)}
                  isInvalid={!!(touched.locale && errors.locale)}
                />
              </FloFormGroup>

              <FloFormGroup
                controlId   = "role"
                label       = {t('Role')}
                touched     = {touched}
                errors      = {errors}
              >
                <FloDropdown
                  id      = "role"
                  title   = {t('Role')}
                  values  = {values.role}
                  disabled = {isSubmitting}
                  full
                  options = {createAccountRolesList(loggedUser).map(role => ({ value: role.roleKey, label: t(`account-${role.roleKey}`) }))}
                  onChange = {e => setField('role', e.value)}
                  isInvalid={!!(touched.role && errors.role)}
                />
              </FloFormGroup>

              <FloFormGroup
                controlId   = "locations"
                label       = {t('Locations')}
                touched     = {touched}
                errors      = {errors}
              >
                <FloGroupedDropdown
                  id              = "locations"
                  title           = {t('Locations')}
                  values          = {values.locations ? values.locations.split(SEPARATOR) : ''}
                  multiple
                  disabled        = {isSubmitting}
                  reinitList      = {() => fetchUserLocations(userId as string, [], 1, '')}
                  isLoading       = {userLocations.isLoading}
                  loadingMessage  = {() => t('Loading...')}
                  full
                  disableMemoryFilter
                  options = {userLocations.rows?.map(loc => ({ value: loc.id || '', label: loc.nickname || loc.address || '--blank--', roles: loc.roles })) || []}
                  isInvalid={!!(touched.locations && errors.locations)}
                  onChange = {e => {
                    addToLocationRoleMap(e);
                    const val = e ? e.map((datum: any) => datum.value).join(SEPARATOR) : '';
                    setField('locations', val)
                  }}
                  page                  = {userLocations.page || 1}
                  hasMore               = {(userLocations.total || 0) > (userLocations.rows?.length || 0)}
                  onLoadOptions         = {(search: string | undefined, page: number) => fetchUserLocations(userId as string, [], page, search)}
                />
              </FloFormGroup>
            </Form>
          )
        }}
      </Formik>
    </>
  )
})

export interface NewUserModalProps extends FloComponentProps {
  show:   boolean;
  onHide: () => void;
  createUser: (user: User) => void;
  isSubmitting: boolean;
  accountId?: string;
  userLocations: ListView<Location>;
  fetchUserLocations: (userId: string, types: LocationType[], page: number, search?: string) => void;
  userId?: string | null;
  loggedUser?: User,
}

export const NewUserModal: React.FC<NewUserModalProps> = React.memo(({createUser, isSubmitting, accountId, userLocations, fetchUserLocations, userId, loggedUser, show, ...rest}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (show && userId) {
      fetchUserLocations(userId, [], 1);
    }
  }, [show]);

  return (
    <FloModal
      {...rest}
      show            = {show}
      name            = "new-user"
      title           = {t('New User')}
      isSubmitting    = {isSubmitting}
      disableButtons  = {isSubmitting}
      body            = {(props) => <NewUserBody
                                      {...props}
                                      createUser          = {createUser}
                                      isSubmitting        = {isSubmitting}
                                      accountId           = {accountId}
                                      userLocations       = {userLocations}
                                      fetchUserLocations  = {fetchUserLocations}
                                      userId              = {userId}
                                      loggedUser          = {loggedUser}
                                    />
                                  }
    />
  );
})

export default NewUserModal;
